import { type FC, useRef, useState } from 'react';

import CheckmarkCircleFilled from '@crehana/icons/CheckmarkCircleFilled';
import CloseCircleFilled from '@crehana/icons/CloseCircleFilled';
import CloseSmallOutline from '@crehana/icons/CloseSmallOutline';
import TriangleAlert from '@crehana/icons/TriangleAlert';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors, Popper } from '@crehana/ui';

import type { IError, ValidationProps } from '../types';
import { WrapperValidation } from './Validation.styles';

const {
  grayDark,
  primaryLight,
  redMain,
  primaryLighter,
  redLight,
  baseLighter,
} = colors;

interface PopperErrorProps extends IError {
  isDark?: boolean;
  onDismiss?: () => void;
}

const PopperError: FC<React.PropsWithChildren<PopperErrorProps>> = ({
  title,
  body,
  button,
  isDark,
  onClick,
  onDismiss,
}) => {
  const shadow = isDark ? '' : 'shadow-1-dp';

  const hover = isDark
    ? 'text-secondary-main hover:text-secondary-light'
    : 'text-primary-main hover:text-primary-lighter';

  return (
    <div className={`${shadow} rounded-5 pt-12 pr-12 pb-20 pl-24 max-w-280`}>
      <div className="flex justify-between pb-8">
        <TriangleAlert
          size={20}
          className="mt-12"
          color={isDark ? redLight : redMain}
        />
        <div
          className="cursor-pointer"
          onClick={onDismiss}
          onKeyDown={event => {
            if (event.key === 'Esc') {
              onDismiss?.();
            }
          }}
          role="button"
          tabIndex={0}
        >
          <CloseSmallOutline size={24} color={baseLighter} />
        </div>
      </div>
      <p className="font-subtitle3 pb-12 whitespace-pre-line">{title}</p>
      <p className="font-body4 pb-12 whitespace-pre-line">{body}</p>
      {button && (
        <button
          onClick={onClick}
          className={`${hover} flex items-center font-link-2 cursor-pointer`}
          type="button"
        >
          {button}
        </button>
      )}
    </div>
  );
};

const Validation: FC<React.PropsWithChildren<ValidationProps>> = ({
  label,
  state,
  isDark,
  popper,
  buttonLabel,
  handleError,
}) => {
  const [showPopper, setShowPopper] = useState(false);
  const popperRef = useRef<HTMLButtonElement>(null);

  const textColor = `text-${isDark ? 'white' : 'base-dark'}`;

  const isEmpty = state === undefined;
  const isSuccess = state;

  const darkColor = !isEmpty
    ? isSuccess
      ? primaryLighter
      : redLight
    : baseLighter;

  const lightColor = !isEmpty ? (isSuccess ? primaryLight : redMain) : grayDark;

  const iconColor = isDark ? darkColor : lightColor;

  const handleClick = () => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (handleError) handleError();
    // biome-ignore lint/style/useBlockStatements: single-line early return
    else setShowPopper(!showPopper);
  };

  return (
    <WrapperValidation
      state={state}
      isDark={isDark}
      className="flex items-center justify-between w-full rounded-10 p-16"
    >
      <div className="flex">
        {state && <CheckmarkCircleFilled size={16} color={iconColor} />}
        {state === false && <CloseCircleFilled size={16} color={iconColor} />}
        <p className={`tw-font-body4 ml-8 ${textColor}`}>{label}</p>
      </div>
      {state === false && (
        <>
          <button onClick={handleClick} type="button" ref={popperRef}>
            <p className="font-subtitle4 text-red-main">{buttonLabel}</p>
          </button>
          {popper && (
            <Popper
              anchorRef={popperRef}
              isVisible={showPopper}
              position="right"
              dismissOnClickOutside
              isDark={isDark}
              arrowProps={{
                visible: true,
                triggerPosition: 'center',
              }}
              onDismiss={() => setShowPopper(false)}
            >
              <PopperError
                isDark={isDark}
                onDismiss={() => setShowPopper(false)}
                {...popper}
              />
            </Popper>
          )}
        </>
      )}
    </WrapperValidation>
  );
};

export default Validation;
